/**
 * Wallet · Product Reviews — the list of every banker-pushed product this
 * device has opened. Rehydrates via /api/j/bulk-summary using the signed
 * tokens stored in wallet-storage.
 *
 * Each row shows:
 *   - Product name + institution/advisor
 *   - Awaiting review status
 *   - Step-based progress (Product overview → NAVI brief → Optional training → Assessment)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { WalletShell, NAVY, GOLD, BG } from "@/components/wallet/WalletShell";
import { listJourneyTokens } from "@/lib/wallet-storage";
import { CheckCircle2, Circle, ChevronRight, Loader2, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const STEPS = [
  { key: "overview",   label: "Product overview" },
  { key: "brief",      label: "NAVI plain-language explanation" },
  { key: "training",   label: "Optional training" },
  { key: "assessment", label: "Understanding assessment" },
];

export default function WalletProductReviews() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const tokens = useMemo(() => listJourneyTokens().map(t => t.token), []);

  useEffect(() => {
    if (tokens.length === 0) { setRows([]); return; }
    axios.post(`${API}/j/bulk-summary`, { tokens })
      .then(r => setRows(r.data || []))
      .catch(e => setError(e?.response?.data?.detail || "Could not load your product reviews."));
  }, [tokens]);

  return (
    <WalletShell activeTab="reviews">
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-8" data-testid="product-reviews-page">
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Your priority</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-1" style={{ color: NAVY }}>
          {rows === null
            ? "Loading your product reviews…"
            : rows.length === 0
            ? "No products in review yet"
            : rows.length === 1
            ? "You have 1 product awaiting review."
            : `You have ${rows.length} products awaiting review.`}
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Understand them before making a decision. Your advisor sees the status — never your answers or learning history.
        </p>

        {error && (
          <div className="mt-6 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-800 p-4">{error}</div>
        )}

        {rows === null && !error && (
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Fetching your open reviews…
          </div>
        )}

        {rows && rows.length === 0 && (
          <EmptyState />
        )}

        {rows && rows.length > 0 && (
          <div className="mt-8 space-y-3" data-testid="reviews-list">
            {rows.map(r => <ReviewRow key={r.journey_id} row={r} />)}
          </div>
        )}

        {rows && rows.length > 0 && (
          <div className="mt-10">
            <h2 className="text-[13px] font-semibold uppercase tracking-widest" style={{ color: NAVY }}>Understand before you decide</h2>
            {rows.map(r => <UnderstandRow key={r.journey_id} row={r} />)}
          </div>
        )}
      </div>
    </WalletShell>
  );
}

function ReviewRow({ row }) {
  const initial = (row.product_name || "?").trim().charAt(0).toUpperCase();
  const targetPath = tokenToPath(row.token);
  return (
    <Link
      to={targetPath}
      data-testid={`review-row-${row.journey_id}`}
      className="block rounded-2xl overflow-hidden group transition"
      style={{ background: NAVY }}
    >
      <div className="px-5 py-4 sm:px-6 sm:py-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center font-display font-semibold text-lg flex-shrink-0" style={{ background: GOLD, color: NAVY }}>
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
            {row.institution || row.advisor_name || "Institution"} · {(row.product_type || "").replace(/_/g, " ")}
          </div>
          <div className="mt-0.5 text-white font-medium text-[15px] truncate">{row.product_name}</div>
          <div className="mt-1 flex items-center gap-1.5 text-white/70 text-[11px]">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
            {row.status === "completed" ? "Review complete" : row.review_status === "interested" ? "Interested" : "Awaiting review"}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-white/50 group-hover:text-white transition flex-shrink-0" />
      </div>
    </Link>
  );
}

function UnderstandRow({ row }) {
  const targetPath = tokenToPath(row.token);
  const completed = {
    overview:   true,                // we always have product basics from banker payload
    brief:      row.has_brief,       // Claude 4.5 brief generated
    training:   row.has_brief && row.assessment_score != null, // proxy: any engagement
    assessment: row.has_assessment,
  };
  return (
    <div className="mt-4 rounded-2xl bg-white border border-slate-100 p-5 shadow-sm" data-testid={`understand-${row.journey_id}`}>
      <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>{row.product_name}</div>
      <ul className="mt-3 space-y-2">
        {STEPS.map(s => {
          const done = completed[s.key];
          return (
            <li key={s.key} className="flex items-center gap-2 text-sm">
              {done
                ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                : <Circle className="w-4 h-4 text-slate-300 flex-shrink-0" />
              }
              <span className={done ? "text-slate-800" : "text-slate-400"}>{s.label}</span>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${row.percent_complete}%`, background: GOLD }} />
        </div>
        <div className="text-[11px] font-mono text-slate-500 tabular-nums">{row.percent_complete}% complete</div>
      </div>
      <Link to={targetPath} data-testid={`understand-cta-${row.journey_id}`} className="mt-3 inline-flex items-center gap-1 text-sm font-medium hover:underline" style={{ color: GOLD }}>
        {row.has_brief ? "Continue review" : "Start review"} →
      </Link>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mt-10 rounded-2xl bg-white border border-slate-100 p-8 text-center" data-testid="reviews-empty">
      <div className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center" style={{ background: `${GOLD}18` }}>
        <Inbox className="w-6 h-6" style={{ color: GOLD }} />
      </div>
      <div className="mt-4 font-display text-lg font-semibold" style={{ color: NAVY }}>Nothing to review yet</div>
      <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
        When your bank or advisor sends you a product for review, it will appear here. You&apos;ll get a link — open it and this passport will keep track for you.
      </p>
    </div>
  );
}

function tokenToPath(token) {
  return `/j/${token}`;
}
